// Phase 33 · 19a — Dashboard.
//
// The day starts with what has already gone wrong, not with what arrived last.
// Four figures, then the work: an attention queue ranked by SLA deadline and
// the roster holding it.
//
// The org-template widget grid is NOT replaced — it still renders beneath the
// 19a surface, so a tenant's configured widgets (and the Settings → Dashboard
// Management screen that edits them) keep working exactly as before. Every
// control the old header carried — date range, print, accessibility, refresh,
// the AI query bar — survives the re-skin.

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Shield } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { useAuthStore } from '../../stores/authStore';
import { useDashboardMetrics, useAgentMetrics, useSlaMetrics, useRecentActivity, analyticsKeys } from '../../hooks/useAnalytics';
import { useAnalyticsRealtime } from '../../hooks/useAnalyticsRealtime';
import { DateRange } from '../../services/analytics.service';
import { useMyDashboard } from '../../hooks/useOrgDashboardTemplates';
import { DashboardConfig } from '../../services/dashboards.service';
import { OrgDashboardTemplate } from '../../services/dashboards.service';
import { useTicketTrends } from '../../hooks/useAnalytics';
import { DashboardGrid } from './DashboardGrid';
import { SingleDatePicker } from '../../components/common/SingleDatePicker';
import { TeamFilterPicker } from '../../components/teams/TeamFilterPicker';
import { NLQueryBar } from './widgets';
import { AttentionList, AgentLoad, StatRow } from './DashboardOverview';
import { firstName, greeting, longDate, openCount } from './overviewData';
import {
  AccessibilityPanel,
  PrintStyles,
  PrintHeader,
  PrintButton,
} from '../../components/accessibility';
import { PageHeaderMeta } from '@components/layouts/PageHeaderMeta';

// Role-based scope helpers
const ADMIN_ROLES = ['admin', 'owner'];
const SUPERVISOR_ROLES = ['supervisor'];

function getUserScope(role: string): 'all' | 'team' | 'self' {
  if (ADMIN_ROLES.includes(role)) return 'all';
  if (SUPERVISOR_ROLES.includes(role)) return 'team';
  return 'self';
}

/** The floating shell: cards sit ON the canvas, they do not butt against it. */
const CARD: React.CSSProperties = { borderRadius: 10, boxShadow: 'var(--co-shadow-card)' };
const CARD_CLASS = 'border border-[var(--co-line)] bg-[var(--co-raised)]';

const CONTROL =
  'flex h-[34px] flex-none items-center gap-[7px] whitespace-nowrap rounded-[10px] border ' +
  'border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[12.5px] ' +
  'text-[var(--co-ink-2)] hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)] ' +
  'disabled:cursor-not-allowed disabled:opacity-50';
const PRIMARY =
  'flex h-[34px] flex-none items-center rounded-[10px] bg-[var(--co-brass-ink)] px-[14px] ' +
  'text-[12.5px] font-bold text-[var(--co-brass-on)] hover:opacity-90';

// Format a Date as YYYY-MM-DD in local time (matches DateRangePicker so the
// calendar popup and the stored start/end strings always agree).
function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Default window when the page loads: Jan 1 of the current year through today.
function getInitialRange(): DateRange {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  return { start: toISODate(start), end: toISODate(now) };
}

// Choose a sensible trend granularity from the span of the selected range.
function getTrendPeriod(range: DateRange): 'day' | 'week' | 'month' {
  const start = new Date(range.start);
  const end = new Date(range.end);
  const days = Math.round((end.getTime() - start.getTime()) / 86_400_000);
  if (days <= 14) return 'day';
  if (days <= 120) return 'week';
  return 'month';
}

// Convert OrgDashboardTemplate to DashboardConfig for DashboardGrid compatibility
function toGridDashboard(template: OrgDashboardTemplate): DashboardConfig {
  return {
    id: template.id,
    tenantId: template.tenantId,
    userId: '',
    name: template.name,
    roleType: template.roleType,
    isDefault: true,
    isOrgDefault: true,
    layout: template.layout,
    settings: template.settings,
    widgets: template.widgets
      .filter(w => w.isEnabled)
      .map(w => ({
        id: w.id,
        tenantId: w.tenantId,
        dashboardId: w.templateId,
        widgetType: w.widgetType,
        title: w.title,
        config: w.config,
        position: w.position,
        isVisible: true,
        createdAt: w.createdAt,
        updatedAt: w.updatedAt,
      })),
    createdAt: template.createdAt,
    updatedAt: template.updatedAt,
  };
}

export default function DashboardPage() {
  const navigate = useNavigate();

  // Auth & role
  const user = useAuthStore((state) => state.user);
  const userRole = user?.role || 'agent';
  const userScope = getUserScope(userRole);
  const isAgent = userScope === 'self';
  // Mirrors the Inbox: an agent has no 'all' view, so their attention queue is
  // their own. A supervisor's 'all' is scoped to their team by the BFF.
  const isLead = !isAgent;

  // UI state
  const [dateRange, setDateRange] = useState<DateRange>(getInitialRange);
  // Team filter: the figures cover that team and its sub-teams.
  const [teamFilter, setTeamFilter] = useState<string | undefined>(undefined);
  // Stable so the memoised widgets don't all re-render on every page render.
  const kpiDrillDown = useMemo(() => ({ teamId: teamFilter }), [teamFilter]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [accessibilityOpen, setAccessibilityOpen] = useState(false);

  // Latest selectable day for the picker (no future dates).
  const today = useMemo(() => toISODate(new Date()), []);
  const queryClient = useQueryClient();
  useAnalyticsRealtime();

  // Navigate to reports page with the selected report type
  const handleNavigateToReport = useCallback((reportType: string, filters?: Record<string, any>) => {
    const params = new URLSearchParams();
    params.set('report', reportType);
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, String(value));
        }
      });
    }
    navigate(`/reports?${params.toString()}`);
  }, [navigate]);

  // ---- Dashboard config from org template ----
  const { data: orgTemplate, isLoading: dashboardLoading } = useMyDashboard();

  // Convert to grid-compatible format
  const gridDashboard = useMemo(
    () => orgTemplate ? toGridDashboard(orgTemplate) : null,
    [orgTemplate],
  );

  // ---- Analytics data ----
  // countsAllTime: Total/Status/Priority/Channel are all-time on the Dashboard
  // (the date picker still drives Avg Response, CSAT, AI, SLA, trends). The
  // Reports page calls this hook without the flag, so it stays date-scoped.
  const { data: metrics, isLoading: metricsLoading, dataUpdatedAt } = useDashboardMetrics(dateRange, true, teamFilter);
  const { data: agentData, isLoading: agentsLoading } = useAgentMetrics(dateRange, { teamId: teamFilter });
  const { data: slaData, isLoading: slaLoading } = useSlaMetrics(dateRange, teamFilter);
  const { data: trendsData } = useTicketTrends(getTrendPeriod(dateRange), dateRange, teamFilter);
  const { data: recentActivityData } = useRecentActivity();

  const dataLoading = metricsLoading || agentsLoading || slaLoading;

  // ---- Handlers ----
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: analyticsKeys.all }),
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'attention'] }),
    ]);
    setIsRefreshing(false);
  }, [queryClient]);

  // The greeting line is dated, and states the scale of the day: how much is
  // open, and how many people are carrying it.
  const open = openCount(metrics?.tickets);
  // A head count belongs to a team view; an agent's dashboard is their own.
  const agentCount = isLead ? agentData?.length ?? null : null;
  const subtitle = [
    longDate(),
    open === null ? null : `${open} open ticket${open === 1 ? '' : 's'}`,
    agentCount ? `across ${agentCount} agent${agentCount === 1 ? '' : 's'}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  // ---- Loading state ----
  if (dashboardLoading) {
    return (
      <div className="flex h-full min-h-0 flex-col gap-[14px] overflow-hidden bg-[var(--co-canvas)] py-4 pr-4">
        <div className={clsx(CARD_CLASS, 'h-[92px] flex-none animate-pulse')} style={CARD} />
        <div className="flex flex-none gap-[14px]">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={clsx(CARD_CLASS, 'h-[120px] flex-1 animate-pulse')} style={CARD} />
          ))}
        </div>
        <div className={clsx(CARD_CLASS, 'min-h-0 flex-1 animate-pulse')} style={CARD} />
      </div>
    );
  }

  return (
    <div className="co-dashboard flex h-full min-h-0 flex-col gap-[14px] overflow-y-auto bg-[var(--co-canvas)] py-4 pr-4">
      {/* Print styles & header */}
      <PrintStyles />
      <PrintHeader title={isAgent ? 'My Dashboard' : 'Dashboard'} />

      {/* ---------- header card ---------- */}
      {/* Header card + its bottom-right Period tab (the Backtrack shape). */}
      <div className="no-print relative flex flex-none flex-col">
      <header
        className={clsx(CARD_CLASS, 'relative px-5 pb-[10px] pt-[16px] 2xl:!rounded-br-none')}
        style={{ ...CARD, borderRadius: 14 }}
      >
        <div className="flex items-center justify-between gap-[10px]">
          <div className="min-w-0 flex-1">
            <h1 className="text-[28px] font-bold leading-[1.08] tracking-[-.018em] text-[var(--co-ink)]">
              {greeting()}, {firstName(user?.name)}
            </h1>
            {/* The scope chip rides with the subline, not with the controls:
                it does not change anything, it qualifies everything — every
                figure below is bounded by it, which is the same job the date
                and the counts beside it are doing. */}
            <div className="mt-[6px] flex flex-wrap items-center gap-x-[10px] gap-y-[6px]">
              <p className="co-mono text-[10.5px] font-medium uppercase tracking-[.13em] text-[var(--co-ink-3)]">
                {subtitle}
              </p>
              <span
                className="co-mono flex flex-none items-center gap-[5px] whitespace-nowrap rounded-full border border-[var(--co-line-strong)] px-[7px] py-[3px] text-[9px] font-semibold uppercase tracking-[.11em] text-[var(--co-ink-3)]"
                title="The scope of every figure on this page"
              >
                <Shield className="h-[11px] w-[11px]" />
                {isAgent ? 'My data' : userScope === 'team' ? 'Team' : 'All data'}
              </span>
            </div>
          </div>

          {/* Connection state and alerts belong to the PAGE, not to this row of
              controls — they report on the session rather than filter the data
              below. Sitting them top-right keeps the control row to things that
              change what is on screen. */}
          <div className="flex h-[34px] flex-none items-center">
            <PageHeaderMeta />
          </div>
        </div>

        {/* Row 2: the page actions, right-aligned. The reporting period
            lives in the tab under the card's bottom-right corner. */}
        <div className="mt-[10px] flex justify-end border-t border-[var(--co-line-subtle)] pt-[10px]">
          <div className="flex w-full flex-col items-start gap-[10px] sm:w-auto sm:items-end">
            <div className="flex flex-wrap items-center gap-[10px] sm:justify-end">
              {/* Print */}
              <PrintButton controlClassName={CONTROL} />

              {/* Accessibility */}
              <button
                type="button"
                onClick={() => setAccessibilityOpen(true)}
                className={CONTROL}
                title="Accessibility settings"
                aria-label="Open accessibility settings"
              >
                A11y
              </button>

              {/* Refresh */}
              <button
                type="button"
                onClick={handleRefresh}
                disabled={dataLoading || isRefreshing}
                className={CONTROL}
                aria-label="Refresh data"
              >
                <RefreshCw className={`h-[14px] w-[14px] ${dataLoading || isRefreshing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Refresh</span>
              </button>

              <button type="button" onClick={() => navigate('/tickets/new')} className={PRIMARY}>
                + New ticket
              </button>
            </div>

          </div>
        </div>
      </header>

      {/* AI bar + Period tab share one row. Wide screens (2xl): the tab hangs
          off the header card's bottom-right edge (Backtrack's search-tab shape)
          and the AI bar fills the rest of the line, 10px under the card. Both
          stay in normal flow and the tab sizes to its controls, so a long team
          name or a wrapped picker pushes the KPI row down instead of being
          drawn over it. Tablet and below: Period card first, then the AI bar,
          each full width. */}
      <div className="mt-[14px] flex flex-col gap-[14px] 2xl:mt-0 2xl:flex-row 2xl:items-start">
      <NLQueryBar
        onNavigateToReport={handleNavigateToReport}
        variant="machine"
        className="order-2 min-w-0 flex-none 2xl:order-1 2xl:mt-[10px] 2xl:flex-1"
      />
      <div
        data-testid="dashboard-period-tab"
        className="relative z-[2] order-1 flex-none rounded-[14px] border border-[var(--co-line)] bg-[var(--co-raised)] px-4 py-3 2xl:order-2 2xl:-mt-px 2xl:max-w-[60%] 2xl:rounded-t-none 2xl:border-t-0 2xl:pb-4 2xl:pt-4"
        style={{ boxShadow: 'var(--co-shadow-card)' }}
      >
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -left-[15px] top-0 hidden h-[15px] w-[15px] 2xl:block"
          style={{
            background:
              'radial-gradient(circle at 0 100%, transparent 14px, var(--co-line) 14px, var(--co-line) 15px, var(--co-raised) 15px)',
          }}
        />
            <div className="flex flex-wrap items-center gap-[10px] 2xl:flex-nowrap 2xl:justify-end">
              <TeamFilterPicker value={teamFilter} onChange={setTeamFilter} />
              <span className="co-mono text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-4)]">
                Period
              </span>
              <SingleDatePicker
                inline
                label="Start"
                value={dateRange.start}
                maxDate={dateRange.end}
                onChange={(start) => setDateRange((r) => ({ ...r, start }))}
              />
              <SingleDatePicker
                inline
                align="right"
                label="End"
                value={dateRange.end}
                minDate={dateRange.start}
                maxDate={today}
                onChange={(end) => setDateRange((r) => ({ ...r, end }))}
              />
            </div>
      </div>
      </div>
      </div>

      {/* Widget Grid (read-only) — the tenant's configured widgets, unchanged.
          This sits ABOVE the fixed surface below it: the grid opens with the
          tenant's own KPI row, and those cards are the ones carrying tone, so
          burying them under a second set of figures put the worst number on the
          page below the fold. */}
      {gridDashboard && gridDashboard.widgets.length > 0 && (
        <div className="flex-none">
          <DashboardGrid
            dashboard={gridDashboard}
            dashboardData={metrics}
            agentData={agentData}
            slaData={slaData}
            trendsData={trendsData}
            recentActivityData={recentActivityData}
            loading={dataLoading}
            dateRange={dateRange}
            isEditMode={false}
            drillDown={kpiDrillDown}
          />
        </div>
      )}

      {/* ---------- four figures ---------- */}
      <StatRow metrics={metrics} sla={slaData} />

      {/* ---------- the work: attention queue + roster ---------- */}
      <div className="flex flex-none flex-col gap-[14px] 2xl:flex-row">
        <AttentionList isLead={isLead} />
        {/* Load across people is a lead's question. On an agent's own view the
            card could only ever list themselves above an empty card. */}
        {isLead && <AgentLoad agents={agentData} loading={agentsLoading} />}
      </div>

      {/* Accessibility Panel */}
      <AccessibilityPanel
        isOpen={accessibilityOpen}
        onClose={() => setAccessibilityOpen(false)}
      />

      {/* Last updated */}
      <div className="co-mono no-print flex-none text-center text-[10px] uppercase tracking-[.1em] text-[var(--co-ink-4)]">
        {dataUpdatedAt
          ? `Last updated ${new Date(dataUpdatedAt).toLocaleTimeString()}`
          : 'Loading…'}
      </div>
    </div>
  );
}
